import classNames from 'classnames';
import React from 'react';
import * as styles from './PostSeriesFooter.module.scss';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import arrowLeftIcon from '../../icons/non-prefilled/16x16/arrowLeft.svg';
import arrowRightIcon from '../../icons/non-prefilled/16x16/arrowRight.svg';
import history from '../../icons/non-prefilled/16x16/history.svg';
import Link from '../../utils/router/Link';
import postPath, { publicPostPath } from '../../utils/url/postPath';
import useIsPublicView from '../../utils/useIsPublicView';

const POST_FRAGMENT = gql(`
  fragment PaginationItem_post on PostFields {
    id
    ...postPath_post
    ...publicPostPath_post
  }
`);

const PAGINATION_ITEM_TYPES = {
  prev: {
    label: 'Prev',
    icon: arrowLeftIcon,
  },
  next: {
    label: 'Next',
    icon: arrowRightIcon,
  },
  restart: {
    label: 'Back to beginning',
    icon: history,
  },
} as const;

interface PaginationItemProps {
  post?: DocumentType<typeof POST_FRAGMENT> | null;
  type: keyof typeof PAGINATION_ITEM_TYPES;
}

const PaginationItem = ({ post, type }: PaginationItemProps) => {
  const isPublicView = useIsPublicView();

  return (
    post && (
      <Link
        to={isPublicView ? publicPostPath(post) : postPath(post)}
        className={classNames(styles.paginationItem, {
          [styles.prev]: type === 'prev',
          [styles.nextOrRestart]: type !== 'prev',
        })}
      >
        <div className={styles.postDetails}>
          <p className={styles.meta}>{PAGINATION_ITEM_TYPES[type].label}</p>
          <p className={styles.postTitle}>{post.title}</p>
        </div>
        <SVGIcon className={styles.icon}>
          {PAGINATION_ITEM_TYPES[type].icon}
        </SVGIcon>
      </Link>
    )
  );
};

const SERIES_FRAGMENT = gql(`
  fragment PostSeriesFooter_series on SeriesFields {
    id
    posts {
      ...PaginationItem_post
    }
  }
`);

interface PostSeriesFooterProps {
  postId: string;
  series: DocumentType<typeof SERIES_FRAGMENT>;
}

const PostSeriesFooter = ({ postId, series }: PostSeriesFooterProps) => {
  const index = series.posts.findIndex((post) => post.id === postId);
  if (index === -1 || series.posts.length <= 1) {
    return null;
  }

  const prev = series.posts[index - 1];
  const nextOrFirst = series.posts[index + 1] ?? series.posts[0];
  const isLastInSeries = index >= series.posts.length - 1;

  return (
    <div className={styles.container}>
      <PaginationItem post={prev} type="prev" />
      {nextOrFirst !== prev && (
        <PaginationItem
          post={nextOrFirst}
          type={isLastInSeries ? 'restart' : 'next'}
        />
      )}
    </div>
  );
};

export default PostSeriesFooter;
